export function getCaliforniaDiligenceSnapshot(property = {}, userTier = 'free') {
  const zone = property.fire_hazard_zone || null;
  const isLosAngeles = property.county === 'Los Angeles';
  const hasAssessorRecord = Boolean(property.ain);
  const hasPortal = Boolean(property.assessor_portal_url);
  const taxStatus = (property.tax_status || '').trim();
  const countyLabel = property.county ? `${property.county} County` : 'County review';

  const riskTone = zone === 'Very High' ? 'high' : zone === 'High' ? 'medium' : 'low';
  const riskLabel = zone ? `${zone} fire zone` : 'Fire zone pending';
  const dataLabel = hasAssessorRecord ? 'Assessor-backed parcel' : (isLosAngeles ? 'Assessor retry recommended' : 'Geocoder-only parcel');
  const coverageLabel = hasAssessorRecord
    ? `${countyLabel} coverage verified`
    : hasPortal
      ? `${countyLabel} portal follow-up`
      : `${countyLabel} manual review`;

  const badges = [
    { label: riskLabel, tone: riskTone },
    { label: dataLabel, tone: hasAssessorRecord ? 'good' : 'neutral' },
    { label: coverageLabel, tone: hasAssessorRecord ? 'good' : 'neutral' },
  ];

  if (hasPortal) {
    badges.push({ label: 'County portal linked', tone: 'good' });
  }

  const bullets = [];

  if (zone === 'Very High') {
    bullets.push('Expect tighter insurance underwriting and verify defensible-space requirements before pricing a deal.');
  } else if (zone === 'High') {
    bullets.push('Insurance costs can move quickly in high-risk California zones, so confirm current coverage assumptions early.');
  } else if (zone === 'Moderate') {
    bullets.push('Fire risk is more manageable here, but insurance quotes can still vary by carrier and micro-location.');
  } else {
    bullets.push('CalFire fire-zone data did not resolve yet, so confirm hazard classification before final underwriting.');
  }

  if (hasAssessorRecord) {
    bullets.push('Cross-check the AIN, legal description, and county tax status before relying on title or value assumptions.');
  } else if (isLosAngeles) {
    bullets.push('Los Angeles County sometimes needs a direct assessor retry; verify the parcel in the county portal before moving forward.');
  } else {
    bullets.push('This county currently resolves through statewide geocoder coverage, so verify parcel details against the county assessor portal.');
  }

  if (hasAssessorRecord && isLosAngeles) {
    bullets.push('Los Angeles coverage is strong enough for first-pass screening, but title, tax, and parcel dimensions should still be confirmed in escrow diligence.');
  } else if (!hasAssessorRecord && hasPortal) {
    bullets.push(`Open the ${countyLabel} assessor portal next so you can confirm ownership, assessed values, and parcel-level notes before outreach.`);
  }

  if (taxStatus && taxStatus.toLowerCase() !== 'current') {
    bullets.push(`County tax status is "${taxStatus}", which should be reviewed before underwriting or outreach.`);
  }

  if (!hasPortal && property.county) {
    bullets.push('No direct county portal link is available yet, so budget extra diligence time for record verification.');
  }

  if (userTier === 'free' && hasAssessorRecord) {
    bullets.push('Unlock owner and assessed-value fields to validate who holds the parcel and how the county values it.');
  }

  return {
    title: 'California Diligence Snapshot',
    summary: hasAssessorRecord
      ? `A quick underwriter-style checklist built from ${countyLabel} parcel, tax, and fire-zone signals.`
      : `Use this checklist to verify ${countyLabel} records before treating the parcel match as final.`,
    badges,
    bullets,
  };
}

export function getCaliforniaRecordTrail(property = {}, userTier = 'free') {
  const county = property.county || 'County review';
  const countyLabel = county.endsWith('County') ? county : `${county} County`;
  const hasAssessorRecord = Boolean(property.ain);
  const hasPortal = Boolean(property.assessor_portal_url);
  const isLosAngeles = property.county === 'Los Angeles';
  const taxStatus = (property.tax_status || '').trim();
  const parcelHandle = property.ain || property.apn || null;

  const confidence = hasAssessorRecord ? 'High confidence' : ((hasPortal || isLosAngeles) ? 'Medium confidence' : 'Low confidence');
  const source = hasAssessorRecord
    ? (isLosAngeles ? 'Los Angeles cached assessor parcel record' : `${countyLabel} assessor-backed parcel record`)
    : 'California statewide geocoder match';
  const verificationStage = hasAssessorRecord
    ? 'Screening ready'
    : hasPortal
      ? 'Portal verification needed'
      : 'Manual county review needed';

  let nextStep = hasAssessorRecord
    ? 'Confirm tax status, parcel dimensions, and legal description inside county records before underwriting.'
    : 'Validate the exact parcel against county records before treating this match as production-ready diligence.';

  if (isLosAngeles && hasAssessorRecord) {
    nextStep = 'Los Angeles is the priority California county here: use the cached parcel record for first-pass screening, then confirm tax status and legal description in the county portal before escrow.';
  } else if (isLosAngeles) {
    nextStep = 'Los Angeles is still the priority county, but this parcel needs a county-record retry before you rely on the statewide match.';
  } else if (hasPortal) {
    nextStep = `Open the ${countyLabel} portal next to confirm ownership, assessed value, and parcel notes before outreach.`;
  }

  const fields = [
    { label: 'Record Source', value: source },
    { label: 'Match Confidence', value: confidence },
    { label: 'Verification Stage', value: verificationStage },
    { label: 'Next Step', value: nextStep },
    {
      label: isLosAngeles ? 'Priority County' : 'Coverage Mode',
      value: isLosAngeles
        ? 'Los Angeles is the first California county with cached assessor parcel coverage in PropertyDeepDive.'
        : `${countyLabel} currently relies on statewide search plus county follow-up.`,
    },
  ];

  if (parcelHandle) {
    fields.splice(3, 0, {
      label: 'Parcel Handle',
      value: isLosAngeles ? `AIN ${parcelHandle}` : `County parcel ${parcelHandle}`,
    });
  }

  if (hasPortal) {
    fields.push({
      label: 'County Portal Action',
      value: userTier === 'free'
        ? `Upgrade to open the ${countyLabel} portal directly from this page before outreach.`
        : `<a href="${property.assessor_portal_url}" target="_blank" rel="noopener">Open ${countyLabel} assessor portal →</a>`,
    });
  }

  if (taxStatus) {
    fields.push({ label: 'County Tax Status', value: taxStatus });
  }

  if (userTier === 'free' && hasAssessorRecord) {
    fields.push({
      label: 'Pro Unlock',
      value: 'Unlock the county portal link and assessed-value fields before moving from screening into outreach.',
    });
  }

  const badges = [
    { label: confidence, tone: hasAssessorRecord ? 'good' : ((hasPortal || isLosAngeles) ? 'medium' : 'neutral') },
    { label: verificationStage, tone: hasAssessorRecord ? 'good' : 'neutral' },
  ];

  if (isLosAngeles) {
    badges.push({ label: 'Los Angeles priority', tone: 'good' });
  }

  return {
    title: 'California Record Trail',
    summary: `Track how this ${countyLabel} parcel was resolved, how much confidence to place in the match, and what to verify next.`,
    badges,
    fields,
  };
}

export function getCaliforniaOfferReadiness(property = {}, userTier = 'free') {
  const county = property.county || 'California';
  const countyLabel = county.endsWith('County') ? county : `${county} County`;
  const hasAssessorRecord = Boolean(property.ain);
  const hasPortal = Boolean(property.assessor_portal_url);
  const taxStatus = (property.tax_status || '').trim();
  const hasTaxStatus = Boolean(taxStatus);
  const taxCurrent = taxStatus.toLowerCase() === 'current';
  const fireZone = property.fire_hazard_zone || null;
  const highFireRisk = ['High', 'Very High'].includes(fireZone);
  const hasAssessedValue = Boolean(property.assessed_land || property.assessed_improvement || property.assessed_total);
  const isPro = userTier !== 'free';

  const checks = [
    {
      key: 'parcel',
      label: 'Parcel match',
      ready: hasAssessorRecord,
      detail: hasAssessorRecord
        ? 'Assessor-backed parcel identity is available for first-pass offer screening.'
        : 'Verify APN/AIN and legal description with the county before treating this as offer-ready.',
      weight: 30,
    },
    {
      key: 'tax',
      label: 'Tax posture',
      ready: hasTaxStatus && taxCurrent,
      detail: hasTaxStatus
        ? `County tax status is ${taxStatus}; confirm installments and supplemental assessments before close.`
        : 'County tax status is missing, so pull the tax record before pricing contingencies.',
      weight: 25,
    },
    {
      key: 'insurance',
      label: 'Insurance screen',
      ready: Boolean(fireZone) && !highFireRisk,
      detail: fireZone
        ? `${fireZone} fire-zone signal found; confirm insurance appetite before waiving diligence.`
        : 'Fire-zone lookup is unresolved, so confirm hazard classification before underwriting.',
      weight: 20,
    },
    {
      key: 'backup',
      label: 'County backup',
      ready: isPro && hasPortal,
      locked: !isPro,
      detail: isPro && hasPortal
        ? `County portal access is available for reconciling ${countyLabel} records.`
        : 'Pro unlocks the direct county portal path for record backup before outreach.',
      weight: 15,
    },
    {
      key: 'values',
      label: 'Value backup',
      ready: isPro && hasAssessedValue,
      locked: !isPro,
      detail: isPro && hasAssessedValue
        ? 'Assessed-value fields are available for comparing offer assumptions against county records.'
        : 'Unlock assessed-value backup before moving from free screening into an offer workflow.',
      weight: 10,
    },
  ];

  const score = checks.reduce((sum, check) => sum + (check.ready ? check.weight : 0), 0);
  const readyCount = checks.filter((check) => check.ready).length;
  const verifyCount = checks.filter((check) => !check.ready && !check.locked).length;
  const lockedCount = checks.filter((check) => check.locked && !check.ready).length;
  const blockers = checks.filter((check) => !check.ready && !check.locked);
  const lockedChecks = checks.filter((check) => check.locked && !check.ready);
  const nextStep = blockers[0] || lockedChecks[0] || checks[0];
  let status = 'Verify before offer';
  let tone = 'medium';
  if (score >= 80) {
    status = 'Offer screen ready';
    tone = 'good';
  } else if (score < 45) {
    status = 'Record backup needed';
    tone = 'high';
  }

  return {
    title: 'California Offer Readiness Meter',
    summary: `A conservative ${countyLabel} offer-screening read on parcel identity, tax posture, insurance risk, and county-record backup.`,
    score,
    status,
    tone,
    counts: {
      ready: readyCount,
      verify: verifyCount,
      locked: lockedCount,
    },
    next_step: {
      label: nextStep.label,
      detail: nextStep.detail,
    },
    checks: checks.map(({ label, ready, locked, detail }) => ({
      label,
      status: ready ? 'ready' : (locked ? 'locked' : 'verify'),
      detail,
    })),
  };
}

export function getCaliforniaEscrowChecklist(property = {}, userTier = 'free') {
  const county = property.county || 'California';
  const countyLabel = county.endsWith('County') ? county : `${county} County`;
  const hasAssessorRecord = Boolean(property.ain);
  const hasPortal = Boolean(property.assessor_portal_url);
  const hasTaxStatus = Boolean((property.tax_status || '').trim());
  const hasFireZone = property.fire_hazard_zone != null;
  const hasAssessedValue = Boolean(property.assessed_land || property.assessed_improvement || property.assessed_total);
  const taxStatus = (property.tax_status || '').trim();
  const fireZone = property.fire_hazard_zone || 'Needs confirmation';
  const isPro = userTier !== 'free';

  const checklist = [
    {
      label: 'Parcel Identity',
      status: hasAssessorRecord ? 'ready' : 'verify',
      detail: hasAssessorRecord
        ? `AIN ${property.ain} is available for first-pass ${countyLabel} escrow screening.`
        : 'Confirm APN/AIN, legal description, and situs address directly with the county before relying on this match.',
    },
    {
      label: 'Tax Posture',
      status: hasTaxStatus && taxStatus.toLowerCase() === 'current' ? 'ready' : 'verify',
      detail: hasTaxStatus
        ? `County tax status is ${taxStatus}; verify installment history and supplemental assessments before close.`
        : 'Pull the county tax record before pricing or waiving diligence deadlines.',
    },
    {
      label: 'Insurance Screen',
      status: hasFireZone && !['High', 'Very High'].includes(fireZone) ? 'ready' : 'verify',
      detail: hasFireZone
        ? `${fireZone} fire-zone signal found; confirm carrier appetite and defensible-space requirements during underwriting.`
        : 'Fire-zone lookup has not resolved, so confirm hazard classification before final underwriting.',
    },
    {
      label: 'Assessor Backup',
      status: isPro && hasPortal ? 'ready' : 'locked',
      detail: isPro && hasPortal
        ? `Open the ${countyLabel} assessor portal and reconcile ownership, assessed values, and parcel notes.`
        : 'Pro users can open county records from this page before outreach or escrow.',
    },
  ];

  if (hasAssessedValue) {
    checklist.push({
      label: 'Value Cross-Check',
      status: isPro ? 'ready' : 'locked',
      detail: isPro
        ? 'Compare assessed land and improvement values against offer assumptions and recent comparable sales.'
        : 'Unlock assessed-value fields before moving from free screening into an offer workflow.',
    });
  }

  const readyCount = checklist.filter((item) => item.status === 'ready').length;
  const verifyCount = checklist.filter((item) => item.status === 'verify').length;
  const lockedCount = checklist.filter((item) => item.status === 'locked').length;
  const nextPriority = checklist.find((item) => item.status === 'verify')
    || checklist.find((item) => item.status === 'locked')
    || checklist[0];

  return {
    title: 'California Escrow Checklist',
    summary: `A practical next-step checklist for turning this ${countyLabel} record into escrow-ready diligence.`,
    badges: [
      { label: `${readyCount} ready`, tone: readyCount ? 'good' : 'neutral' },
      { label: `${verifyCount} verify`, tone: verifyCount ? 'medium' : 'good' },
      { label: `${lockedCount} Pro-gated`, tone: lockedCount ? 'neutral' : 'good' },
    ],
    next_priority: nextPriority
      ? {
          label: nextPriority.label,
          status: nextPriority.status,
          detail: nextPriority.detail,
        }
      : null,
    checklist,
  };
}

export function getCaliforniaRiskTriage(property = {}, userTier = 'free') {
  const county = property.county || 'California';
  const countyLabel = county.endsWith('County') ? county : `${county} County`;
  const taxStatus = (property.tax_status || '').trim();
  const fireZone = property.fire_hazard_zone || null;
  const hasAssessorRecord = Boolean(property.ain);
  const hasPortal = Boolean(property.assessor_portal_url);
  const hasAssessedValue = Boolean(property.assessed_land || property.assessed_improvement || property.assessed_total);
  const isPro = userTier !== 'free';

  const risks = [
    {
      label: 'Parcel confidence',
      level: hasAssessorRecord ? 'clear' : 'verify',
      detail: hasAssessorRecord
        ? `Assessor-backed ${countyLabel} parcel identity is available for screening.`
        : 'Treat this as a geocoder match until APN/AIN and legal description are confirmed in county records.',
    },
    {
      label: 'Tax posture',
      level: taxStatus && taxStatus.toLowerCase() === 'current' ? 'clear' : 'verify',
      detail: taxStatus
        ? `County tax status is ${taxStatus}; verify installment details before relying on close-cost assumptions.`
        : 'Tax status is missing, so pull the county record before offer, escrow, or investor outreach.',
    },
    {
      label: 'Insurance pressure',
      level: ['High', 'Very High'].includes(fireZone) ? 'escalate' : (fireZone ? 'clear' : 'verify'),
      detail: fireZone
        ? `${fireZone} fire-zone signal found; confirm current insurance availability and pricing early.`
        : 'Fire-zone lookup has not resolved, so confirm hazard classification before final underwriting.',
    },
    {
      label: 'County backup',
      level: isPro && hasPortal ? 'clear' : 'locked',
      detail: isPro && hasPortal
        ? `County portal backup is available for reconciling ${countyLabel} records.`
        : 'Pro unlocks the direct county-record path for portal backup before outreach or escrow.',
    },
    {
      label: 'Value backup',
      level: isPro && hasAssessedValue ? 'clear' : 'locked',
      detail: isPro && hasAssessedValue
        ? 'Assessed-value backup is available for comparing county value signals against deal assumptions.'
        : 'Unlock assessed-value backup before moving from screening into an offer or client recommendation.',
    },
  ];

  const counts = {
    clear: risks.filter((risk) => risk.level === 'clear').length,
    verify: risks.filter((risk) => risk.level === 'verify').length,
    escalate: risks.filter((risk) => risk.level === 'escalate').length,
    locked: risks.filter((risk) => risk.level === 'locked').length,
  };
  const clearancePriority = { escalate: 0, verify: 1, locked: 2, clear: 3 };
  const clearancePlan = risks
    .slice()
    .sort((a, b) => (clearancePriority[a.level] ?? 9) - (clearancePriority[b.level] ?? 9))
    .slice(0, 3)
    .map((risk, index) => ({
      step: index + 1,
      label: risk.label,
      level: risk.level,
      detail: risk.level === 'clear'
        ? `${risk.label} is clear enough for first-pass screening; keep the record in the deal file before comparing offers.`
        : risk.detail,
    }));

  let status = 'Clear for first-pass screening';
  let tone = 'good';
  if (counts.escalate > 0) {
    status = 'Escalate before offer';
    tone = 'high';
  } else if (counts.verify > 0) {
    status = 'Pause for verification';
    tone = 'medium';
  } else if (counts.locked > 0) {
    status = 'Pro backup recommended';
    tone = 'neutral';
  }

  const nextPriority = risks.find((risk) => risk.level === 'escalate')
    || risks.find((risk) => risk.level === 'verify')
    || risks.find((risk) => risk.level === 'locked')
    || risks[0];

  return {
    title: 'California Risk Triage',
    summary: `A conservative ${countyLabel} read on the next risk that can change a tour, offer, escrow, or client recommendation.`,
    status,
    tone,
    counts,
    badges: [
      { label: `${counts.clear} clear`, tone: counts.clear ? 'good' : 'neutral' },
      { label: `${counts.verify} verify`, tone: counts.verify ? 'medium' : 'good' },
      { label: `${counts.escalate} escalate`, tone: counts.escalate ? 'high' : 'good' },
      { label: `${counts.locked} Pro-gated`, tone: counts.locked ? 'neutral' : 'good' },
    ],
    next_priority: nextPriority
      ? {
          label: nextPriority.label,
          level: nextPriority.level,
          detail: nextPriority.detail,
        }
      : null,
    clearance_plan: clearancePlan,
    risks,
  };
}
